<script lang="ts">
    import tabIcon from "@/assets/narvarostatistik.svg";

    export default {
        name: "FormUnderlag",
        tabName: "Underlag",
        icon: tabIcon,
    };
</script>

<script setup lang="ts">
    import { computed, ref, toRefs } from "vue";
    import emitter from "tiny-emitter/instance";
    import { AxiosError } from "axios";
    import { useStore } from "@/store";
    import { flashMessage, flashWarning } from "@/utils";
    import { useKForm } from "@/composables/useKForm";
    import KForm from "@/components/forms/Form/KForm.vue";
    import KHeader from "@/components/KHeader.vue";
    import KText from "@/components/forms/inputs/Text/KText.vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
        parameters?: {
            id: string;
            namn: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);

    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const formRef = ref();

    const isEditing = computed(() => !!parameters?.value?.id);

    const heading = computed(() => (isEditing.value ? "Ändra på underlag" : "Skapa nytt underlag"));

    const submitLabel = computed(() => (isEditing.value ? "Uppdatera underlag" : "Skapa underlag"));

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData?.());

    defineExpose({ hasUnsavedData, tabId });

    const initForm = () => ({ ...parameters?.value });

    const onSubmit = async (formData: any) => {
        const underlag = isEditing.value ? { ...formData, id: parameters?.value?.id } : formData;
        try {
            if (isEditing.value) {
                await store.dispatch("skreg/underlag/updateUnderlag", { underlag });
            } else {
                await store.dispatch("skreg/underlag/createUnderlag", { underlag });
            }
            flashMessage(isEditing.value ? "Underlaget har uppdaterats" : "Underlaget har skapats", 3000);
            store.dispatch("tabs/closeTab", tabId.value);
        } catch (thrownError) {
            const error = thrownError as AxiosError;
            if (error?.response?.data) {
                handleErrorResponse(error);
                return;
            }
            flashWarning(isEditing.value ? "Kunde inte uppdatera underlaget." : "Kunde inte spara underlaget.");
        }
    };

    const onCancel = ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        if (hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    { title: "Fortsätt redigera", type: "secondary" },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => store.dispatch("tabs/closeTab", tabId.value),
                    },
                ],
            });
            return;
        }
        store.dispatch("tabs/closeTab", tabId.value);
    };
</script>

<template>
    <div class="underlag-form">
        <KForm
            ref="formRef"
            id="underlag-form"
            :initial-values-getter="initForm"
            :submit-content="submitLabel"
            cancel-content="Avbryt"
            :disableSubmit="!hasUnsavedData"
            :errors="inputErrors"
            :formErrors="formErrors"
            @submit="onSubmit"
            @cancel="onCancel"
        >
            <template #header>
                <KHeader :heading="heading" />
            </template>
            <template #default>
                <div class="form-content">
                    <KText name="namn" label="Namn" validation="required" />
                </div>
            </template>
        </KForm>
    </div>
</template>

<style lang="scss" scoped></style>
